import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "../data/site";

const SITE = "WWW.KASHETTER.COM";
const NAME = "M/s. Kashetter Industrial Essentials";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <section className="bg-[#0A192F] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-white/70 hover:text-[#0EA5E9] mb-6" data-testid="back-home-from-privacy">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="slash-divider"></span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0EA5E9]">Legal</span>
          </div>
          <h1 className="font-head font-black text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl">Last updated: {new Date().getFullYear()}</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 legal-body">
          <p>
            At {NAME} (accessible at <strong>{SITE}</strong>), the privacy of our visitors and
            customers is important to us. This Privacy Policy document outlines the types of
            information we collect and how we use it.
          </p>

          <h2>Information We Collect</h2>
          <p>We may collect the following information when you use our website or contact us:</p>
          <ul>
            <li>Name, company name, phone number and email address submitted through our enquiry form.</li>
            <li>Product interest, specifications and messages you send us.</li>
            <li>Standard log data such as IP address, browser type, referrer URL and time of access, collected automatically for security and analytics.</li>
            <li>Cookie data (see the Cookies section below).</li>
          </ul>

          <h2>How We Use Your Information</h2>
          <p>We use the information we collect only for the following purposes:</p>
          <ul>
            <li>To respond to your enquiries and provide quotations for our products.</li>
            <li>To dispatch products, invoices and delivery communication.</li>
            <li>To improve our website content, product listings and customer service.</li>
            <li>To send periodic updates about products, offers or company news — only if you have consented.</li>
            <li>To comply with applicable Indian laws and tax regulations.</li>
          </ul>

          <h2>Cookies</h2>
          <p>
            Our website uses cookies to remember your preferences and to analyse how visitors use
            the site. Cookies are small text files stored on your device by your web browser. You
            can choose to disable cookies through your browser settings, but doing so may limit
            your ability to use certain features of the website.
          </p>

          <h2>Sharing of Information</h2>
          <p>
            We do <strong>not</strong> sell, rent or trade your personal information to third
            parties. Your information is used only by {NAME} and its authorised employees for the
            purposes described above. We may share limited information with our logistics partners
            solely for the purpose of dispatching your order.
          </p>

          <h2>Data Security</h2>
          <p>
            We implement reasonable security measures to protect the personal information we
            collect from loss, misuse or unauthorised access. Enquiries submitted through our
            website are transmitted securely and are stored in a protected environment. However,
            no method of transmission over the Internet or method of electronic storage is 100%
            secure.
          </p>

          <h2>Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites (for example brand pages, social
            media). We are not responsible for the privacy practices of these external sites, and
            we encourage you to read their privacy policies before providing any information.
          </p>

          <h2>Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Ask what personal information we hold about you.</li>
            <li>Request correction of any incorrect or outdated information.</li>
            <li>Request deletion of your personal information from our records (where legally permissible).</li>
            <li>Withdraw your consent to receive marketing communications at any time.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{" "}
            <a href={COMPANY.emailHref} className="text-[#0EA5E9] underline">{COMPANY.email}</a>.
          </p>

          <h2>Children's Privacy</h2>
          <p>
            Our website and services are intended for business users. We do not knowingly collect
            personal information from children under the age of 18. If you believe we have
            collected such information, please contact us immediately so that we can remove it.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our
            practices or legal requirements. Any updates will be posted on this page with a
            revised "Last updated" date. Continued use of our website after such changes
            constitutes your acceptance of the revised policy.
          </p>

          <h2>Contact</h2>
          <p>
            For any questions about this Privacy Policy, or to make a request regarding your
            personal information, please contact:
          </p>
          <p>
            <strong>{NAME}</strong>
            <br />
            {COMPANY.address}
            <br />
            Phone:{" "}
            <a href={COMPANY.phoneHref} className="text-[#0EA5E9] underline">{COMPANY.phone}</a>
            <br />
            Email:{" "}
            <a href={COMPANY.emailHref} className="text-[#0EA5E9] underline">{COMPANY.email}</a>
          </p>
          <p>
            By using our website, you consent to the collection and use of your information as
            described in this Privacy Policy. Please also read our{" "}
            <Link to="/terms" className="text-[#0EA5E9] underline">Terms &amp; Conditions</Link>.
          </p>
        </div>
      </section>

      <style>{`
        .legal-body { color: #334155; font-size: 15.5px; line-height: 1.75; }
        .legal-body h2 { font-family: 'Chivo', sans-serif; font-weight: 900; color: #0A192F; font-size: 1.5rem; margin-top: 2.25rem; margin-bottom: 0.75rem; letter-spacing: -0.01em; }
        .legal-body p { margin: 0 0 1rem 0; }
        .legal-body ul { margin: 0.25rem 0 1.25rem 1.25rem; }
        .legal-body li { list-style: disc; margin: 0.25rem 0; }
        .legal-body strong { color: #0A192F; }
      `}</style>
    </div>
  );
}
